import { useEffect, useSyncExternalStore } from 'react';
import documentService from '../services/documentService';
import {
  getBillUploadSettingsSnapshot,
  setBillUploadSettings,
  subscribeBillUploadSettings,
  type BillUploadSettings,
} from '../constants/uiVisibility';
import { hasClientAuthSession } from '../utils/sessionAuth';

// The admin can change the setting at any time; reload it at most this often while the app is in use.
const REFRESH_MS = 60 * 1000;
let lastLoadedAt = 0;
let inFlight: Promise<void> | null = null;

export const loadBillUploadSettings = async (force = false): Promise<void> => {
  if (!hasClientAuthSession()) return;
  if (!force && Date.now() - lastLoadedAt < REFRESH_MS) return;
  if (inFlight) return inFlight;
  inFlight = documentService.getUploadSettings()
    .then((settings) => {
      lastLoadedAt = Date.now();
      if (settings) setBillUploadSettings(settings);
    })
    .catch(() => {
      // Keep the last known (or build-time) setting; the API still enforces the real one.
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

/**
 * Current bill upload settings (null until loaded). Re-renders when they change; reloads them when
 * `refreshKey` changes (e.g. on navigation) and when the window regains focus.
 */
export const useBillUploadSettings = (refreshKey?: unknown): BillUploadSettings | null => {
  const settings = useSyncExternalStore(subscribeBillUploadSettings, getBillUploadSettingsSnapshot);

  useEffect(() => {
    void loadBillUploadSettings();
  }, [refreshKey]);

  useEffect(() => {
    const onFocus = () => {
      void loadBillUploadSettings();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  return settings;
};
